import AsyncControl from '@/Controls/AsyncControl';
import ComponentManager from '@/Management/ComponentManager';
import ExpanderGroupBoxViewModelType from '@/ViewModels/Controls/ExpanderGroupBoxViewModel';
import ControlFactory from '@/Factories/ControlFactory';
import GroupFactory from '@/Factories/GroupFactory';
import LayoutRenderer from '@/Utility/LayoutRenderer';
import LinkType from '@/Controls/Link';
import * as CoreFrameDTOs from "coreframetypings/dto";
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import GroupFactoryTuple from '@/Factories/GroupFactoryTuple';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';

export default class ExpanderGroupBox extends AsyncControl {

    private Link = ComponentManager.getControl<typeof LinkType>('Link');
    private jQueryControl: JQuery;
    private content: JQuery;
    private link: LinkType;
    private isDVItemDisplayed: KnockoutObservable<boolean>;

    public constructor(private viewModel: ExpanderGroupBoxViewModelType, private element?: JQuery) {
        super(viewModel, element);
        this.isDVItemDisplayed = this.observableOrDefault(!viewModel.isCollapsedWithAncestors(), true);
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');

        if (this.jQueryControl.find('.c-content').length > 0) {
            this.content = this.jQueryControl.find('.c-content');
        } else {
            this.content = $('<div class="c-content">').appendTo(this.jQueryControl);
        }

        const tuples = this.observableOrDefault([]) as KnockoutObservableArray<ControlFactoryTuple | GroupFactoryTuple>;
        await this.context.executeBusilyAsync(async () => {
            const createdItems = await this.createItemsAsync(this.viewModel.detailViewItems, this.createDvItemAsync);
            if (this.isDisposed || this.viewModel.isDisposed || this.viewModel.context.isDisposed)
                return;

            for (const {controlFactory, tuple} of createdItems) {
                tuples.push(tuple);
                this._createdFactoriesToDispose.push(controlFactory);
            }

            this.viewModel.updateLabel();

            const createdGroups = await this.createItemsAsync(this.viewModel.detailViewGroups, this.createGroupAsync);
            if (this.isDisposed)
                return;

            for (const {groupFactory, tuple} of createdGroups) {
                tuples.push(tuple);
                this._createdFactoriesToDispose.push(groupFactory);
            }

            const $layout = await this.createItemAsync(null, () => this.createLayout(tuples()));
            if (this.isDisposed)
                return;

            this.content.append($layout);
        });

        this.applyBindings(this.jQueryControl, {
            controlName: 'expandergroupbox',

            loader: this.context.isBusy,

            controlIdent: this.identifier,

            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (this.viewModel.isCollapsed()) {
                        states.push('collapsed');
                    }
                    else {
                        states.push('expanded');
                    }

                    return states;
                }
            }),

            visible: this.definePureComputed({
                read: () => {
                    const vmVisible = this.viewModel.isVisible();
                    const isAnyFactoryVisible = tuples().filter(t => t.factory?.isVisible()).length > 0;
                    const isContextVisible = this.viewModel.context == null ? this.context?.isVisible() : false;
                    return vmVisible && (isAnyFactoryVisible || isContextVisible);
                }
            }),

            attr: {
                role: 'group'
            }
        });

        this.applyBindings(this.content, {
            slide: this.definePureComputed({
                read: () => {
                    return {
                        value: !this.viewModel.isCollapsed(),
                        onSlideDown: () => {
                            if (this.isDisposed)
                                return;

                            this.content.find('.k-splitter').each((index: number, splitter: HTMLElement) => {
                                $(splitter).resize();
                            });
                            this.isDVItemDisplayed(true);
                        },
                        onSlideUp: () => {
                            if (this.isDisposed)
                                return;

                            this.isDVItemDisplayed(false);
                        }
                    };
                }
            }),
            locator: this.defineComputed(() => {
                if (this.viewModel.locator() != '')
                    return this.viewModel.locator() + '.content';

                return null;
            })
        });

        if (this.viewModel.link != null) {
            this.link = new this.Link(this.viewModel.link, $('<span>').prependTo(this.jQueryControl));
            let tuple = this.link.getTuple();

            this.applyBindings(tuple.control, {
                attr: {
                    role: 'heading',
                    'aria-level': this.viewModel.level
                }
            });
        }

        this.widget(null);

        return this.jQueryControl;
    }

    private _createdFactoriesToDispose = [];

    private async createDvItemAsync(detailViewItem: CoreFrameDTOs.IDVGItemDTO) {
        return ControlFactory.createInstanceAsync(detailViewItem, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator,
            isDisplayed: this.defineComputed(() => {
                if (this.isDisposed)
                    return false;
                const isCollapsedWithAncestors = this.viewModel.isCollapsedWithAncestors();
                const isDisplayed = this.viewModel.isDisplayed();
                // Ein-/Ausblendung ueber bspw. einen UI-Listener aendert ebenfalls eine Sichtbarkeit, welche bekannt gemacht werden muss
                const isVisible = this.viewModel.isVisible();
                const dvItemDisplayed = this.isDVItemDisplayed();

                return !isCollapsedWithAncestors && isDisplayed && isVisible && dvItemDisplayed;
            })
        });
    }

    private async createGroupAsync(detailViewGroup: CoreFrameDTOs.IDetailViewGroupDTO) {
        return GroupFactory.createInstanceAsync(detailViewGroup, this.viewModel.context, {
            isSummaryViewMode: this.viewModel.isSummaryViewMode,
            locator: this.viewModel.locator,
            ancestorIsCollapsed: this.viewModel.isCollapsedWithAncestors,
            level: this.viewModel.level + 1
        });
    }

    private createLayout(tuples: (ControlFactoryTuple | GroupFactoryTuple)[]) {
        return LayoutRenderer.renderLayout(this.viewModel.layout, tuples, this.viewModel.context instanceof DetailViewContext ? this.viewModel.context.labelColumnWidth : null);
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('ExpanderGroupBox', ExpanderGroupBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');